// ============================================================================
// APPROVAL DOCUMENT UTILITIES
// ============================================================================
// Utility functions for transforming ticket data to approval document format

import type { DASTicketDetails, ApprovalLevel, TicketAttachment, ConsolidatedChildItem, WorkflowAction } from '../../../../../api/types/dasTickets.types';
import type { ApprovalDocumentData, ApprovalDocumentWorkflowStep, ApprovalDocumentConsolidation, ApprovalDocumentConsolidatedItem, ApprovalDocumentConsolidationRef } from './approvalDocument.types';
import { getDocumentMetadata } from '../../../../../config/tenant.config';

/**
 * Configuration for document generation
 */
export interface DocumentGenerationConfig {
  companyName?: string;
  systemName?: string;
  logoUrl?: string;
  disclaimer?: string;
  /** Base URL for verification (document ID will be appended) */
  verificationBaseUrl?: string;
}

/**
 * Cleans up duplicated comments in approval text and removes prefixes like "Reason:" and "Approver Comments:"
 * Handles various legacy data formats where the same text was repeated:
 * 1. "X\n\nReason: Y\n\nApprover Comments: Y" -> "X\n\nY" (keeps prefix text, removes duplicate)
 * 2. "Reason: X\n\nApprover Comments: X" -> "X"
 * 3. "Reason: X\nX" -> "X"
 * 4. "Reason: X" -> "X"
 * 5. "X. X" or "X.X" or "X. X." (period separator) -> "X"
 * 6. "X\nX" (newline separator) -> "X"
 * 7. "X X" (space separator for short text) -> "X"
 * 8. General duplication detection with any separator
 */
const cleanupDuplicatedComments = (text: string | undefined): string | undefined => {
  if (!text) return text;

  let cleanedText = text.trim();

  console.log('[cleanupDuplicatedComments] Input:', JSON.stringify(cleanedText));

  // Pattern E (NEW - Most specific): Direct "Reason: X\nApprover Comments: X" with single newline
  // e.g., "Reason: Lack of document support\nApprover Comments: Lack of document support"
  const singleNewlineMatch = cleanedText.match(/^Reason:\s*([\s\S]+?)\s*\nApprover Comments:\s*([\s\S]+?)$/);
  if (singleNewlineMatch) {
    const reason = singleNewlineMatch[1].trim();
    const comments = singleNewlineMatch[2].trim();
    console.log('[cleanupDuplicatedComments] Pattern E matched - single newline:', { reason, comments });
    if (reason === comments) {
      console.log('[cleanupDuplicatedComments] Pattern E: Returning reason only');
      return reason;
    } else {
      return `${reason}\n\n${comments}`;
    }
  }

  // Pattern F (NEW): Handle prefix + single newline separators
  // e.g., "Rejected via consolidation CON-123\nReason: X\nApprover Comments: X"
  const prefixSingleNewlineMatch = cleanedText.match(/^([\s\S]+?)\nReason:\s*([\s\S]+?)\s*\nApprover Comments:\s*([\s\S]+?)$/);
  if (prefixSingleNewlineMatch) {
    const prefix = prefixSingleNewlineMatch[1].trim();
    const reason = prefixSingleNewlineMatch[2].trim();
    const comments = prefixSingleNewlineMatch[3].trim();
    console.log('[cleanupDuplicatedComments] Pattern F matched - prefix with single newlines:', { prefix, reason, comments });
    if (reason === comments) {
      cleanedText = `${prefix}\n\n${reason}`;
      console.log('[cleanupDuplicatedComments] Pattern F: Returning prefix + reason');
      return cleanedText;
    } else {
      return `${prefix}\n\n${reason}\n\n${comments}`;
    }
  }

  // Pattern A: Handle text with prefix content followed by "Reason: X\n\nApprover Comments: X"
  // e.g., "Rejected via consolidation CON-123\n\nReason: Lack of support\n\nApprover Comments: Lack of support"
  const prefixReasonCommentsMatch = cleanedText.match(/^([\s\S]+?)\n\nReason:\s*([\s\S]+?)\n\nApprover Comments:\s*([\s\S]+?)$/);
  if (prefixReasonCommentsMatch) {
    const prefix = prefixReasonCommentsMatch[1].trim();
    const reason = prefixReasonCommentsMatch[2].trim();
    const comments = prefixReasonCommentsMatch[3].trim();
    console.log('[cleanupDuplicatedComments] Pattern A matched:', { prefix, reason, comments });
    // If reason and comments are the same, keep prefix + reason only
    if (reason === comments) {
      cleanedText = `${prefix}\n\n${reason}`;
    } else {
      // Keep all parts but remove the "Reason:" and "Approver Comments:" labels
      cleanedText = `${prefix}\n\n${reason}\n\n${comments}`;
    }
    return cleanedText;
  }

  // Pattern B: Handle "Reason: X\n\nApprover Comments: Y" format (at start of text)
  const reasonAndCommentsMatch = cleanedText.match(/^Reason:\s*([\s\S]+?)\n\nApprover Comments:\s*([\s\S]+?)$/);
  if (reasonAndCommentsMatch) {
    const reason = reasonAndCommentsMatch[1].trim();
    const comments = reasonAndCommentsMatch[2].trim();
    console.log('[cleanupDuplicatedComments] Pattern B matched:', { reason, comments });
    // If reason and comments are the same, just return the reason
    if (reason === comments) {
      return reason;
    } else {
      // Return both without prefixes, separated by newline
      return `${reason}\n\n${comments}`;
    }
  }

  // Pattern G (NEW): Generic pattern - find and remove duplicate Approver Comments when same as Reason
  // This handles any format with "Reason:" and "Approver Comments:" labels
  if (cleanedText.includes('Reason:') && cleanedText.includes('Approver Comments:')) {
    // Extract content after each label, handling multiline content
    const reasonIdx = cleanedText.indexOf('Reason:');
    const commentsIdx = cleanedText.indexOf('Approver Comments:');

    if (reasonIdx !== -1 && commentsIdx !== -1 && reasonIdx < commentsIdx) {
      // Get text between "Reason:" and "Approver Comments:"
      const reasonStart = reasonIdx + 'Reason:'.length;
      const reasonContent = cleanedText.substring(reasonStart, commentsIdx).trim();

      // Get text after "Approver Comments:"
      const commentsStart = commentsIdx + 'Approver Comments:'.length;
      const commentsContent = cleanedText.substring(commentsStart).trim();

      console.log('[cleanupDuplicatedComments] Pattern G check:', { reasonContent, commentsContent });

      if (reasonContent === commentsContent) {
        // Get any prefix text before "Reason:"
        const prefixText = cleanedText.substring(0, reasonIdx).trim();

        if (prefixText) {
          console.log('[cleanupDuplicatedComments] Pattern G: Returning prefix + reason');
          return `${prefixText}\n\n${reasonContent}`;
        } else {
          console.log('[cleanupDuplicatedComments] Pattern G: Returning reason only');
          return reasonContent;
        }
      }
    }
  }

  // Pattern C: Handle "Reason: X" format (strip the prefix)
  if (cleanedText.startsWith('Reason:')) {
    cleanedText = cleanedText.substring(7).trim();
    console.log('[cleanupDuplicatedComments] Pattern C: Stripped Reason prefix');
  }

  // Pattern D: Handle text with Reason and Approver Comments anywhere (with other content before/between)
  // Look for "Reason: X" followed later by "Approver Comments: X" (same content)
  const reasonMatch = cleanedText.match(/Reason:\s*([^\n]+)/);
  const commentsMatch = cleanedText.match(/Approver Comments:\s*([^\n]+)/);
  if (reasonMatch && commentsMatch) {
    const reasonText = reasonMatch[1].trim();
    const commentsText = commentsMatch[1].trim();
    console.log('[cleanupDuplicatedComments] Pattern D check:', { reasonText, commentsText });
    if (reasonText === commentsText) {
      // Remove the "Approver Comments: X" line entirely
      cleanedText = cleanedText.replace(/\n*Approver Comments:\s*[^\n]+/, '');
      // Also remove the "Reason:" prefix
      cleanedText = cleanedText.replace(/Reason:\s*/, '');
      cleanedText = cleanedText.trim();
      console.log('[cleanupDuplicatedComments] Pattern D: Removed duplicates');
      return cleanedText;
    }
  }

  // Now handle simple duplication patterns on the cleaned text

  // Pattern 1: Period separator "X. X" or "X.X" or "X. X." (with optional trailing period)
  const periodParts = cleanedText.split(/[.\u3002\uff0e]\s*/).filter(part => part.trim() !== '');
  if (periodParts.length === 2) {
    const part1 = periodParts[0].trim();
    const part2 = periodParts[1].trim();
    if (part1 && part2 && part1 === part2) {
      return part1;
    }
  }
  if (periodParts.length > 2) {
    const firstPart = periodParts[0].trim();
    const allSame = periodParts.every(part => part.trim() === firstPart);
    if (allSame && firstPart) {
      return firstPart;
    }
  }

  // Pattern 2: Newline separator "X\nX" or multiple newlines
  const newlineParts = cleanedText.split(/\n+/).filter(part => part.trim() !== '');
  if (newlineParts.length === 2) {
    const part1 = newlineParts[0].trim();
    const part2 = newlineParts[1].trim();
    if (part1 && part2 && part1 === part2) {
      return part1;
    }
  }
  if (newlineParts.length > 2) {
    const firstPart = newlineParts[0].trim();
    const allSame = newlineParts.every(part => part.trim() === firstPart);
    if (allSame && firstPart) {
      return firstPart;
    }
  }

  // Pattern 3: Space separator for exactly duplicated text "X X"
  const halfLength = Math.floor(cleanedText.length / 2);
  if (cleanedText.length > 1 && cleanedText.length % 2 === 1) {
    const firstHalf = cleanedText.substring(0, halfLength).trim();
    const secondHalf = cleanedText.substring(halfLength + 1).trim();
    if (firstHalf && secondHalf && firstHalf === secondHalf && cleanedText[halfLength] === ' ') {
      return firstHalf;
    }
  }

  // Pattern 4: General duplication detection
  const generalSeparatorParts = cleanedText.split(/[\s]*[.,;:\-–—|/\\]+[\s]*/).filter(part => part.trim() !== '');
  if (generalSeparatorParts.length === 2) {
    const part1 = generalSeparatorParts[0].trim();
    const part2 = generalSeparatorParts[1].trim();
    if (part1 && part2 && part1 === part2) {
      return part1;
    }
  }

  return cleanedText;
};

// Document defaults are built PER CALL, never at module scope.
//
// This module is imported during app bootstrap, before login has run, so a
// module-level `getDocumentMetadata()` would read an empty sessionStorage and
// freeze whatever it found into the config for the lifetime of the page. That
// is what previously stamped every certificate with the branding that was
// resolved before the tenant was known. Resolving here means each generated
// document reflects the tenant that is signed in right now.
const buildDefaultConfig = (): DocumentGenerationConfig => {
  const tenantMetadata = getDocumentMetadata();

  return {
    companyName: tenantMetadata.companyName,
    systemName: tenantMetadata.systemName,
    logoUrl: tenantMetadata.logoUrl,
    disclaimer: tenantMetadata.companyName
      ? `This is a system-generated document. No physical signature is required. Verified by ${tenantMetadata.companyName} ${tenantMetadata.systemName}.`
      : `This is a system-generated document. No physical signature is required. Verified by ${tenantMetadata.systemName}.`,
  };
};

/**
 * Generates a document ID based on ticket number and date
 */
const generateDocumentId = (ticketNo: string): string => {
  return ticketNo;
};

/**
 * Finds the final approval action from workflow history
 * Used when approval hierarchy data is incomplete (e.g., consolidation approvals)
 */
const findFinalApprovalFromHistory = (
  workflowHistory: WorkflowAction[],
  isRejected: boolean
): WorkflowAction | null => {
  // Sort by date descending to find the most recent action first
  const sortedHistory = [...workflowHistory].sort(
    (a, b) => new Date(b.actionDate).getTime() - new Date(a.actionDate).getTime()
  );

  // Priority 1: Look for explicit final approval/rejection actions (case-insensitive)
  const finalAction = sortedHistory.find((action) => {
    const actionLower = action.action.toLowerCase();
    // Match 'FinalApproved', 'Final Approved', 'FinalRejected', 'Final Rejected'
    return actionLower === 'finalapproved' ||
           actionLower === 'final approved' ||
           actionLower === 'finalrejected' ||
           actionLower === 'final rejected' ||
           actionLower.includes('final approv') ||
           actionLower.includes('final reject');
  });

  if (finalAction) return finalAction;

  // Priority 2: For non-final actions, find the last approval/rejection that is NOT a pre-approval
  const targetAction = isRejected ? 'reject' : 'approv';
  return sortedHistory.find((action) => {
    const actionLower = action.action.toLowerCase();
    // Exclude pre-approvals and consolidation-related actions
    const isPreApproval = actionLower.includes('pre') || actionLower.includes('consolidation');
    return actionLower.includes(targetAction) && !isPreApproval;
  }) || null;
};

/**
 * Finds the final approver from the approval hierarchy
 * Falls back to workflow history if approval hierarchy is incomplete
 */
const findFinalApprover = (
  approvalHierarchy: ApprovalLevel[],
  workflowHistory: WorkflowAction[] = [],
  isRejected: boolean = false
): ApprovalLevel | null => {
  // First, look for explicit final approver with completed status
  const explicitFinal = approvalHierarchy.find(
    (level) => level.isFinalApprover && (level.status === 'Approved' || level.status === 'Rejected')
  );
  if (explicitFinal) return explicitFinal;

  // Otherwise, find the last approved/rejected level
  const completedLevels = approvalHierarchy.filter(
    (level) => level.status === 'Approved' || level.status === 'Rejected'
  );
  if (completedLevels.length > 0) {
    return completedLevels.reduce((last, current) => (current.level > last.level ? current : last));
  }

  // If approval hierarchy is incomplete, try to get data from workflow history
  // This handles cases like consolidation approvals where tbl_request_approvers isn't updated
  if (workflowHistory.length > 0) {
    const finalAction = findFinalApprovalFromHistory(workflowHistory, isRejected);
    if (finalAction) {
      // Find the corresponding approval level (even if status is Pending)
      const finalApproverLevel = approvalHierarchy.find((level) => level.isFinalApprover);
      if (finalApproverLevel) {
        // Return a merged version with workflow history data
        // Apply cleanup to comments from workflow history
        return {
          ...finalApproverLevel,
          status: isRejected ? 'Rejected' : 'Approved',
          actionDate: finalAction.actionDate,
          comments: cleanupDuplicatedComments(finalAction.comments),
          approverName: finalAction.actionByName || finalAction.actionBy || finalApproverLevel.approverName,
          approverRole: finalAction.actionByRole || finalApproverLevel.approverRole,
        };
      }

      // If no final approver level found, create one from workflow history
      // Apply cleanup to comments from workflow history
      return {
        level: Math.max(...approvalHierarchy.map((l) => l.level), 0) + 1,
        levelName: 'Final Approval',
        approverName: finalAction.actionByName || finalAction.actionBy,
        approverRole: finalAction.actionByRole,
        status: isRejected ? 'Rejected' : 'Approved',
        actionDate: finalAction.actionDate,
        comments: cleanupDuplicatedComments(finalAction.comments),
        isFinalApprover: true,
      };
    }
  }

  return null;
};

/**
 * Transforms approval hierarchy to workflow steps
 * Merges workflow history data when approval hierarchy is incomplete
 */
const transformToWorkflowSteps = (
  approvalHierarchy: ApprovalLevel[],
  workflowHistory: WorkflowAction[] = [],
  ticketIsApproved: boolean = false,
  ticketIsRejected: boolean = false
): ApprovalDocumentWorkflowStep[] => {
  // Sort by level, with final approvers at the end
  const sorted = [...approvalHierarchy].sort((a, b) => {
    if (a.isFinalApprover && !b.isFinalApprover) return 1;
    if (!a.isFinalApprover && b.isFinalApprover) return -1;
    return a.level - b.level;
  });

  // Create a map of workflow history by action type for quick lookup
  const historyByApprover = new Map<string, WorkflowAction>();
  workflowHistory.forEach((action) => {
    const key = (action.actionByName || action.actionBy).toLowerCase();
    // Keep the most recent action for each approver
    if (!historyByApprover.has(key) ||
        new Date(action.actionDate) > new Date(historyByApprover.get(key)!.actionDate)) {
      historyByApprover.set(key, action);
    }
  });

  // Find final approval action from history
  const finalApprovalAction = workflowHistory.find((action) => {
    const actionLower = action.action.toLowerCase();
    // Match 'FinalApproved', 'Final Approved', 'FinalRejected', 'Final Rejected'
    return actionLower === 'finalapproved' ||
           actionLower === 'final approved' ||
           actionLower === 'finalrejected' ||
           actionLower === 'final rejected' ||
           actionLower.includes('final approv') ||
           actionLower.includes('final reject');
  });

  return sorted.map((level, index) => {
    let decision = level.status as 'Approved' | 'Rejected' | 'Pending' | 'Skipped';
    let decisionDateTime = level.actionDate;
    let comments = level.comments;

    // Handle Pre Approved status - map it to Approved for the document
    if (level.status === 'Pre Approved') {
      decision = 'Approved';
    }

    // If status is Pending but ticket is completed, update the decision
    if (level.status === 'Pending' && (ticketIsApproved || ticketIsRejected)) {
      // Check if this is the final approver
      if (level.isFinalApprover) {
        decision = ticketIsRejected ? 'Rejected' : 'Approved';
      }
    }

    // Try to get date/time and comments from workflow history if missing
    // This handles cases where approval hierarchy doesn't have the action details
    const needsHistoryData = !decisionDateTime || !comments;
    const isCompletedStatus = decision === 'Approved' || decision === 'Rejected';

    if (needsHistoryData && isCompletedStatus) {
      // For final approvers, use the final approval action from history
      if (level.isFinalApprover && finalApprovalAction) {
        if (!decisionDateTime) {
          decisionDateTime = finalApprovalAction.actionDate;
        }
        if (!comments) {
          comments = finalApprovalAction.comments;
        }
      } else {
        // Try to find matching workflow history entry by approver name
        const approverKey = level.approverName.toLowerCase();
        const matchingHistory = historyByApprover.get(approverKey);

        if (matchingHistory) {
          if (!decisionDateTime) {
            decisionDateTime = matchingHistory.actionDate;
          }
          if (!comments) {
            comments = matchingHistory.comments;
          }
        }
      }
    }

    return {
      stepNumber: index + 1,
      approverName: level.approverName,
      approverRole: level.approverRole,
      decision,
      decisionDateTime,
      comments: cleanupDuplicatedComments(comments),
      isFinalApprover: level.isFinalApprover,
      isPreApprover: level.isPreApprover,
    };
  });
};

/**
 * Transforms attachments to document attachment format
 */
const transformAttachments = (attachments: TicketAttachment[]): { fileName: string; title?: string; description?: string; fileType?: string }[] => {
  return attachments.map((attachment) => ({
    fileName: attachment.fileName,
    title: attachment.documentTitle,
    description: attachment.documentDescription,
    fileType: attachment.fileType,
  }));
};

/**
 * Transforms consolidated child items to document format
 */
const transformConsolidatedItems = (
  items: ConsolidatedChildItem[]
): ApprovalDocumentConsolidatedItem[] => {
  return items.map((item, index) => ({
    itemNumber: index + 1,
    requestId: item.ticketNo,
    subject: item.subject,
    value: item.value,
    status: item.status,
    submittedBy: item.createdByUserName,
    preApprovedBy: item.preApprovedByName,
    preApprovedAt: item.preApprovedAt,
  }));
};

/**
 * Transforms consolidation data from ticket
 */
const transformConsolidation = (
  ticket: DASTicketDetails
): ApprovalDocumentConsolidation | undefined => {
  // Check if this is a PARENT consolidated request (not a child item approved via consolidation)
  // Only these indicators mean this IS a consolidated request:
  // 1. isConsolidatedRequest flag = true
  // 2. consolidatedItemCount > 0
  // 3. Has consolidated items in the array
  // NOTE: Do NOT use currentStage containing 'consolidation' as an indicator!
  // A stage like "Approved via consolidation" means the ticket was APPROVED BY a consolidation,
  // not that it IS a consolidation request itself.
  const hasItems = (ticket.consolidatedItems?.length || 0) > 0;
  const hasCount = ticket.consolidatedItemCount !== undefined && ticket.consolidatedItemCount > 0;

  const isConsolidated = ticket.isConsolidatedRequest || hasItems || hasCount;

  if (!isConsolidated) {
    console.log('[ApprovalDocument] Not a consolidated request:', {
      isConsolidatedRequest: ticket.isConsolidatedRequest,
      consolidatedItemCount: ticket.consolidatedItemCount,
      hasItems,
      currentStage: ticket.currentStage,
    });
    return undefined;
  }

  const items = ticket.consolidatedItems || [];
  const totalValue = ticket.consolidatedTotalValue || items.reduce((sum, item) => sum + (item.value || 0), 0);

  console.log('[ApprovalDocument] Consolidation data:', {
    isConsolidated: true,
    totalItems: ticket.consolidatedItemCount || items.length,
    totalValue,
    itemsCount: items.length,
    items: items.map(i => ({ ticketNo: i.ticketNo, subject: i.subject })),
  });

  // Only return consolidation data if we have items to show
  // For approval documents, don't show an empty "Consolidated Items" section
  if (items.length === 0) {
    console.log('[ApprovalDocument] Consolidation detected but no items to display - hiding section');
    return undefined;
  }

  return {
    isConsolidated: true,
    totalItems: ticket.consolidatedItemCount || items.length,
    totalValue: totalValue > 0 ? totalValue : undefined,
    items: transformConsolidatedItems(items),
  };
};

/**
 * Extracts consolidation reference for individual requests approved/rejected via consolidation
 * Checks currentStage, workflow history, and remarks for consolidation request number
 */
const extractConsolidationRef = (
  ticket: DASTicketDetails,
  remarks?: string
): ApprovalDocumentConsolidationRef | undefined => {
  const currentStageLower = ticket.currentStage.toLowerCase();

  // Check if this individual request was approved/rejected via consolidation
  const isApprovedViaConsolidation =
    currentStageLower === 'closed - approved via consolidation' ||
    currentStageLower === 'closed - rejected via consolidation' ||
    currentStageLower.includes('approved via consolidation') ||
    currentStageLower.includes('rejected via consolidation');

  if (!isApprovedViaConsolidation) {
    return undefined;
  }

  // Try to extract consolidation request number from various sources
  let consolidationRequestNumber: string | undefined;

  // Pattern: "via consolidation CON-XXXXXX-XXXX" or "via consolidation CON-123"
  const conPattern = /via consolidation\s+(CON-[\w-]+)/i;

  // Check remarks first
  if (remarks) {
    const remarksMatch = remarks.match(conPattern);
    if (remarksMatch) {
      consolidationRequestNumber = remarksMatch[1];
    }
  }

  // Check workflow history comments if not found in remarks
  if (!consolidationRequestNumber && ticket.workflowHistory) {
    for (const action of ticket.workflowHistory) {
      if (action.comments) {
        const match = action.comments.match(conPattern);
        if (match) {
          consolidationRequestNumber = match[1];
          break;
        }
      }
    }
  }

  // Check currentStage if it contains the number
  if (!consolidationRequestNumber) {
    const stageMatch = ticket.currentStage.match(conPattern);
    if (stageMatch) {
      consolidationRequestNumber = stageMatch[1];
    }
  }

  console.log('[ApprovalDocument] Consolidation reference:', {
    isApprovedViaConsolidation,
    consolidationRequestNumber,
    currentStage: ticket.currentStage,
  });

  return {
    isApprovedViaConsolidation: true,
    consolidationRequestNumber,
  };
};

/**
 * Transforms DASTicketDetails to ApprovalDocumentData
 *
 * @param ticket - The ticket details from the DAS system
 * @param config - Optional configuration for document generation
 * @returns ApprovalDocumentData ready for the document preview
 */
export const transformTicketToApprovalDocument = (
  ticket: DASTicketDetails,
  config?: DocumentGenerationConfig
): ApprovalDocumentData | null => {
  const mergedConfig = { ...buildDefaultConfig(), ...config };

  // Check if ticket has completed approval
  // Handle direct approval/rejection and consolidation-based closures
  const currentStageLower = ticket.currentStage.toLowerCase();
  const isApproved =
    ticket.currentStage === 'Approved' ||
    currentStageLower.includes('approved') ||
    currentStageLower === 'closed - approved via consolidation';
  const isRejected =
    ticket.currentStage === 'Rejected' ||
    currentStageLower.includes('rejected') ||
    currentStageLower === 'closed - rejected via consolidation';

  if (!isApproved && !isRejected) {
    console.warn('Cannot generate approval document for ticket that is not approved or rejected');
    return null;
  }

  const workflowHistory = ticket.workflowHistory || [];

  // Find the final approver (with fallback to workflow history)
  const finalApproverLevel = findFinalApprover(
    ticket.approvalHierarchy || [],
    workflowHistory,
    isRejected
  );

  if (!finalApproverLevel) {
    console.warn('Cannot find final approver for the ticket');
    return null;
  }

  const now = new Date().toISOString();

  // Try to get final approval date/time and comments from workflow history if not in approval level
  let finalDecisionDateTime = finalApproverLevel.actionDate;
  let finalRemarks = finalApproverLevel.comments;

  console.log('[ApprovalDocument] Final approval initial data:', {
    approverName: finalApproverLevel.approverName,
    approverRole: finalApproverLevel.approverRole,
    actionDate: finalApproverLevel.actionDate,
    comments: finalApproverLevel.comments,
    status: finalApproverLevel.status,
  });

  if (!finalDecisionDateTime || !finalRemarks) {
    // Find final approval action from workflow history using the helper function
    const finalApprovalAction = findFinalApprovalFromHistory(workflowHistory, isRejected);

    console.log('[ApprovalDocument] Looking for final approval in workflow history:', {
      found: !!finalApprovalAction,
      action: finalApprovalAction?.action,
      actionDate: finalApprovalAction?.actionDate,
      comments: finalApprovalAction?.comments,
      actionBy: finalApprovalAction?.actionByName || finalApprovalAction?.actionBy,
    });

    if (finalApprovalAction) {
      if (!finalDecisionDateTime) {
        finalDecisionDateTime = finalApprovalAction.actionDate;
      }
      if (!finalRemarks) {
        finalRemarks = finalApprovalAction.comments;
      }
    }
  }

  console.log('[ApprovalDocument] Final approval result:', {
    approverName: finalApproverLevel.approverName,
    decisionDateTime: finalDecisionDateTime,
    remarks: finalRemarks,
  });

  // Fall back to ticket's last action date if still not found
  if (!finalDecisionDateTime) {
    finalDecisionDateTime = ticket.lastActionDate || now;
  }

  return {
    metadata: {
      documentTitle: isRejected ? 'Final Rejection Certificate' : 'Final Approval Certificate',
      documentId: generateDocumentId(ticket.ticketNo),
      generatedOn: now,
      companyName: mergedConfig.companyName!,
      systemName: mergedConfig.systemName!,
      logoUrl: mergedConfig.logoUrl,
      verificationUrl: mergedConfig.verificationBaseUrl
        ? `${mergedConfig.verificationBaseUrl}${encodeURIComponent(ticket.ticketNo)}`
        : undefined,
    },
    request: {
      requestId: ticket.ticketNo,
      requestType: ticket.requestType,
      subject: ticket.subject,
      submittedBy: ticket.createdByUserName,
      submittedByDepartment: undefined, // Add if available in ticket
      submittedOn: ticket.createdAt || '',
      value: ticket.value,
      description: ticket.description,
    },
    finalApproval: {
      approverName: finalApproverLevel.approverName,
      approverRole: finalApproverLevel.approverRole,
      decision: isRejected ? 'Rejected' : 'Approved',
      decisionDateTime: finalDecisionDateTime,
      remarks: cleanupDuplicatedComments(finalRemarks),
    },
    workflowSteps: transformToWorkflowSteps(
      ticket.approvalHierarchy || [],
      workflowHistory,
      isApproved,
      isRejected
    ),
    attachments: ticket.attachments ? transformAttachments(ticket.attachments) : undefined,
    consolidation: transformConsolidation(ticket),
    consolidationRef: extractConsolidationRef(ticket, finalRemarks),
    disclaimer: mergedConfig.disclaimer,
  };
};

